import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { baseURL } from '../../../api/api';
import { dictionaryApi, disertationsApi } from '../../api/endpoints';
import DataTable from '../../components/DataTable';
import useAdminList from '../../hooks/useAdminList';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const DisertationsList = () => {
    const { toast, confirm } = useUi();
    const list = useAdminList(disertationsApi.list, { sort: 'id', dir: 'desc' });
    const { reload } = list;
    const [specs, setSpecs] = useState([]);

    useEffect(() => {
        let cancelled = false;
        dictionaryApi
            .options('dis-spec')
            .then((items) => !cancelled && setSpecs(items))
            .catch(() => {
                /* фильтр по специальности не критичен — список всё равно работает */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleDelete = useCallback(
        async (row) => {
            const ok = await confirm({
                title: 'Удалить диссертацию?',
                message: `«${row.name}»\n\nЗапись и её PDF будут удалены безвозвратно.`,
                confirmLabel: 'Удалить',
                danger: true,
            });
            if (!ok) return;

            try {
                await disertationsApi.remove(row.id);
                toast.success('Диссертация удалена');
                reload();
            } catch (e) {
                toast.error(e.message);
            }
        },
        [confirm, toast, reload]
    );

    const columns = [
        { key: 'id', title: 'ID', width: 70, sortable: true },
        {
            key: 'name',
            title: 'Название',
            sortable: true,
            render: (row) => (
                <div>
                    <NavLink to={`/admin/disertations/${row.id}`} className={styles.primaryLink}>
                        {row.name || <span className="adm-muted">без названия</span>}
                    </NavLink>
                    {row.authors.length > 0 && (
                        <div className={styles.subline}>{row.authors.map((a) => a.name).join(', ')}</div>
                    )}
                </div>
            ),
        },
        {
            key: 'spec',
            title: 'Специальность',
            width: 260,
            render: (row) =>
                row.spec ? (
                    <div>
                        <div>{row.spec.name}</div>
                        {row.spec.napravlenie && (
                            <div className={styles.subline}>{row.spec.napravlenie.name}</div>
                        )}
                    </div>
                ) : (
                    <span className="adm-badge adm-badge-warning">не указана</span>
                ),
        },
        { key: 'year', title: 'Год', width: 90, sortable: true, render: (row) => row.year || '—' },
        {
            key: 'file_path',
            title: 'Файл',
            width: 90,
            render: (row) =>
                row.file_path ? (
                    <a href={baseURL + row.file_path} target="_blank" rel="noopener noreferrer">
                        PDF ↗
                    </a>
                ) : (
                    <span className="adm-badge adm-badge-warning">нет</span>
                ),
        },
        {
            key: 'actions',
            title: '',
            width: 150,
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <NavLink to={`/admin/disertations/${row.id}`} className="adm-btn adm-btn-ghost adm-btn-sm">
                        Изменить
                    </NavLink>
                    <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Удалить
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">Диссертации</h1>
                    <p className="adm-page-subtitle">Диссертации и авторефераты по специальностям</p>
                </div>
                <NavLink to="/admin/disertations/new" className="adm-btn adm-btn-primary">
                    + Добавить диссертацию
                </NavLink>
            </div>

            <DataTable
                columns={columns}
                rows={list.rows}
                loading={list.loading}
                error={list.error}
                onRetry={list.reload}
                search={list.search}
                onSearchChange={list.setSearch}
                searchPlaceholder="Поиск по названию и оглавлению"
                sort={list.sort}
                dir={list.dir}
                onSortChange={list.toggleSort}
                page={list.page}
                perPage={list.perPage}
                total={list.total}
                lastPage={list.lastPage}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
                emptyText={list.search ? 'По запросу ничего не найдено' : 'Диссертаций пока нет'}
                filters={
                    <select
                        className={styles.filterSelect}
                        value={list.filters.spec_id === undefined ? '' : list.filters.spec_id}
                        onChange={(e) => list.setFilter('spec_id', e.target.value)}
                        aria-label="Фильтр по специальности"
                    >
                        <option value="">Все специальности</option>
                        {specs.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                }
            />
        </>
    );
};

export default DisertationsList;
